/**
 * Clés de cache du domaine `draft`.
 *
 * Voir `src/hooks/profile/keys.ts` pour le pourquoi de la fabrique.
 *
 * TROIS ENTRÉES SEULEMENT, POUR UN SLICE DE 650 LIGNES
 *
 * `draftslice` ne chargeait en réalité que deux choses — la liste complète des
 * brouillons (`draftLoad`, qui parcourt toutes les pages) et la liste des
 * catégories — auxquelles `teamDraftSlice` ajoutait les brouillons d'une équipe.
 * Tout le reste était des mutations, et la moitié du slice servait à recopier à
 * la main leur résultat dans `state.draft.drafts` : dix-huit reducers qui
 * cherchaient un brouillon par identifiant pour lui repiquer un champ.
 *
 * Ces recopies disparaissent au profit de l'invalidation. Une exception,
 * documentée dans `useEditDraftMutation` : la remontée de média, où recharger
 * depuis l'API afficherait une image pas encore téléversée.
 */
export const draftKeys = {
  all: ['draft'] as const,

  /** La liste complète des brouillons de l'utilisateur. */
  list: () => [...draftKeys.all, 'list'] as const,

  /**
   * Un brouillon chargé seul, par `GET /draft/{id}`.
   *
   * Volontairement à l'écart de `list()` : c'est le repli pour un brouillon que
   * la liste paginée ne ramène pas (cf. `useDraftQuery`), et le verser dans la
   * liste le ferait réapparaître dans les vues qui la filtrent.
   */
  detail: (draftId: string | undefined) => [...draftKeys.all, 'detail', draftId] as const,

  /** Les brouillons d'une équipe (ex-`teamDraftSlice`). */
  team: (teamId: string | undefined) => [...draftKeys.all, 'team', teamId] as const,

  /** Toutes les listes d'équipe, cible d'invalidation. */
  teams: () => [...draftKeys.all, 'team'] as const,

  categories: () => [...draftKeys.all, 'categories'] as const,

  /** `pagination.total` de `PaginateDraftCommand` : le décompte serveur des brouillons (cf. `useDraftCountQuery`). */
  count: () => [...draftKeys.all, 'count'] as const,

  /** `GET /draft/:id/history` : les versions successives d'un brouillon. */
  history: (draftId: string | undefined) => [...draftKeys.all, 'history', draftId] as const,
  /** `GET /draft/:id/events` : la chronologie de programmation (date, commentaires, repartages). */
  events: (draftId: string | undefined) => [...draftKeys.all, 'events', draftId] as const,

  /** `GET /draft/{id}/public` : un brouillon partagé, lu sans session (cf. `usePublicDraftQuery`). */
  publicDetail: (draftId: string | undefined) => [...draftKeys.all, 'public', draftId] as const,

  /** Préfixe des mutations, pour `useMutationState` (cf. `useSavingDrafts`). */
  mutations: () => [...draftKeys.all, 'mutation'] as const,
} as const;
